import * as p from '@clack/prompts'
import type { BetterstartIntegrationId } from '@cli-types/integration-engine/integrations.js'
import type { PresetPromptOptions, PresetPromptResult } from '@cli-types/next-adapter/init.js'
import type { CollectedIntegrationConfig } from '@cli-types/next-adapter/runtime.js'
import { readProjectEnvVar } from '@cli-utils/files.js'
import { persistBlobReadWriteToken } from '@next-adapter/init/scaffolders/env/persist-blob-read-write-token.js'
import { collectIntegrationConfig } from '@next-adapter/integration-runtime/collect-integration-config.js'
import pc from 'picocolors'
import { maskBlobToken } from './mask-blob-token.js'

export async function promptPresets(
  cwd: string,
  options: PresetPromptOptions = {}
): Promise<PresetPromptResult> {
  const sections: CollectedIntegrationConfig['sections'] = []
  const overwriteKeys = new Set<string>()
  const mergeIntegrationConfig = (collected: CollectedIntegrationConfig): void => {
    sections.push(...collected.sections)
    for (const key of collected.overwriteKeys) {
      overwriteKeys.add(key)
    }
  }

  const storage = await p.select({
    message: 'Choose a file storage',
    options: [
      {
        value: 'vercel-blob' as const,
        label: 'Vercel Blob'
      },
      {
        value: 'r2' as const,
        label: 'Cloudflare R2'
      },
      {
        value: 'railway-bucket' as const,
        label: 'Railway Bucket',
        hint: 'Private S3-compatible storage with signed reads'
      },
      {
        value: 'local' as const,
        label: 'Local filesystem (public/media)',
        hint: '▲ For testing only'
      }
    ],
    initialValue: 'vercel-blob' as const
  })

  if (p.isCancel(storage)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  if (storage === 'r2') {
    mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['r2']))
  } else if (storage === 'railway-bucket') {
    const flow = await options.provisionRailwayBucket?.()
    if (flow?.ok && flow.config) {
      mergeIntegrationConfig(flow.config)
    } else if (flow) {
      p.log.warn(
        'Continuing without Railway bucket credentials — rerun betterstart add --integration railway-bucket after fixing Railway access.'
      )
    } else {
      mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['railway-bucket']))
    }
  } else if (storage === 'vercel-blob') {
    const existingToken = readProjectEnvVar(cwd, 'BLOB_READ_WRITE_TOKEN')?.trim()
    const flow =
      !existingToken && options.provisionVercelBlob
        ? await options.provisionVercelBlob()
        : undefined

    if (flow?.ok && flow.token) {
      persistBlobReadWriteToken(cwd, flow.token)
      sections.push({
        header: 'Storage (Vercel Blob)',
        vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: flow.token }]
      })
      overwriteKeys.add('BLOB_READ_WRITE_TOKEN')
    } else if (flow) {
      p.log.warn(
        'Continuing without a Blob token — add BLOB_READ_WRITE_TOKEN to .env.local from the store on vercel.com/dashboard/stores.'
      )
      sections.push({
        header: 'Storage (Vercel Blob)',
        vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: '' }]
      })
    } else {
      if (existingToken) {
        p.log.info(
          `Using the existing BLOB_READ_WRITE_TOKEN from .env.local ${pc.dim(`(${maskBlobToken(existingToken)})`)}`
        )
      }
      mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['vercel-blob']))
    }
  }

  const selectedPresets = await p.multiselect({
    message: 'Select presets',
    options: [
      {
        value: 'blog' as const,
        label: 'Blog',
        hint: 'Posts'
      }
    ],
    required: false,
    initialValues: ['blog' as const]
  })

  if (p.isCancel(selectedPresets)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  const integrations: BetterstartIntegrationId[] = []

  sections.push({
    header: 'Email (Resend)',
    vars: [
      { key: 'BETTERSTART_RESEND_API_KEY', value: '' },
      { key: 'BETTERSTART_EMAIL_FROM', value: '' }
    ]
  })

  if (storage === 'r2') {
    integrations.push('r2')
  } else if (storage === 'railway-bucket') {
    integrations.push('railway-bucket')
  } else if (storage === 'vercel-blob') {
    integrations.push('vercel-blob')
  }

  return {
    presets: selectedPresets,
    integrations,
    storage,
    integrationConfig: { sections, overwriteKeys }
  }
}
